import { Component, Prop, h, Host } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

/**
 * Related links feature grid section for SignNow brand.
 * Displays a heading followed by a responsive grid of link-cards (`features-card-type-5`).
 * Each card shows a check-circle icon, a heading-styled link (`<me-button>`), and a description.
 *
 * Content can be supplied in two ways:
 * - Via `items` + `constants` props (server-side / CMS rendering)
 * - Via named slots `heading` and `cards` (inline / Storybook usage)
 *
 * When `items` is provided the component renders cards internally from the prop data.
 * Otherwise it falls back to rendering whatever is in the `cards` slot.
 *
 * @product signnow
 * @source relatedlinks
 * @version 2.1.0
 * @category SignNow
 * @constant FEATURES_TYPE_12_HEADING - "Discover the powerful office signature feature with airSlate SignNow" - Section heading (used when items prop is provided)
 * @slot heading - <me-heading tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading (used when items prop is not provided)
 * @slot cards - {multiple}, <div class="features-card-type-5"> - Feature link card items with heading-styled `me-button` links (used when items prop is not provided)
 * @example
 *   <sn-features-type-2
 *     constants='{"FEATURES_TYPE_12_HEADING": "Discover powerful features with airSlate SignNow"}'
 *     items='[{"title": "Sign documents online", "description": "Easily sign any document.", "link": "/features/sign", "type": "other"}]'
 *   ></sn-features-type-2>
 */
@Component({
  tag: 'sn-features-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNFeaturesType2 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Content alignment.
   * @default "center"
   */
  @Prop() contentAlign: 'left' | 'center' = 'center';

  /**
   * HTML tag for the heading element.
   * @default "h2"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h2';

  /**
   * Component constants object — must contain `FEATURES_TYPE_12_HEADING`.
   * @content
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * Feature link card items.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Card heading text (also the link label)
   * @item.property {string} link - URL the heading links to
   * @item.property {string} description - Card description text
   * @item.property {string} [type] - Optional source type tag (edu, wiki, other, …)
   * @item.required title
   * @item.required link
   * @item.required description
   * @item.content title description
   * @minItems 1
   * @maxItems 4
   */
  @Prop({ mutable: true }) items?: any[] | string;

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants, {});
    this.items = parseJsonProp(this.items, null);
  }

  render() {
    const itemsArray: any[] | null = Array.isArray(this.items) ? this.items : null;
    const hasItems = itemsArray !== null && itemsArray.length > 0;

    let cardsModifier = '';
    if (hasItems) {
      if (itemsArray.length === 3) cardsModifier = ' features-type-2__cards--triple';
      if (itemsArray.length === 4) cardsModifier = ' features-type-2__cards--quad';
    }

    const sectionClass = `features-type-2${this.contentAlign === 'center' ? ' features-type-2--centered' : ''}`;

    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class={sectionClass} yGap="5xl" yGapLg="6xl" role="region" aria-label="Features">
          <me-row>
            <me-column span="12">
              {hasItems ? (
                <me-heading align="center" variant="2" tag={this.headingTag}>
                  {getConstant(this.constants, 'FEATURES_TYPE_12_HEADING', 'Discover the powerful office signature feature with airSlate SignNow')}
                </me-heading>
              ) : (
                <slot name="heading"></slot>
              )}
            </me-column>
            <me-column spanLg="12" class={`features-type-2__cards${cardsModifier}`} itemScope itemType="https://schema.org/ItemList">
              {hasItems ? (
                itemsArray.map((item, index) => (
                  <div class="features-card-type-5" itemScope itemType="https://schema.org/ListItem" itemProp="itemListElement" key={String(index)}>
                    <div class="features-card-type-5__icon">
                      <me-icon name="interface--check-circle"></me-icon>
                    </div>
                    <me-heading tag="h3" class="features-card-type-5__title">
                      <me-button class="features-card-type-5__title-link" tag="a" variant="ghost" href={item.link || ''}>
                        {item.title}
                      </me-button>
                    </me-heading>
                    {item.description && <me-text>{item.description}</me-text>}
                  </div>
                ))
              ) : (
                <slot name="cards"></slot>
              )}
            </me-column>
          </me-row>
        </me-section>
      </Host>
    );
  }
}
